import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useIntl } from 'react-intl';

import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useDeleteEntity } from 'src/features/entity/api/useDeleteEntity';
import { LIST_ENTITIES_RESOURCE } from 'src/features/entity/api/useListEntities';
import { entityMessages } from 'src/features/entity/utils/messages';
import { Entity, ENTITY_TYPE } from 'src/features/entity/types';
import { useNavigate, useParams } from 'react-router-dom';
import { useParking } from 'src/providers/ParkingProvider';

interface DeleteEntityProps<TData> extends DataTableNewEntryProps<TData> {
    selectedEntity: Entity;
    children?: React.ReactNode;
}

function DeleteAllowedTime<TData>({ selectedEntity, children }: DeleteEntityProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { type = '' } = useParams();
    const navigate = useNavigate();
    const { parkingId } = useParking();

    const entityToDeleteTranslation = type === ENTITY_TYPE.PERSON ? entityMessages.entity : entityMessages.group;

    const { mutate: deleteEntity } = useDeleteEntity({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ENTITIES_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(entityMessages.deleteEntitySuccess),
                description: formatMessage(entityMessages.deleteEntitySuccessDescription, {
                    entry: selectedEntity.name,
                }),
            });
            navigate(-1);
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: {
                    ...entityMessages.deleteEntity,
                    values: {entry: formatMessage(entityToDeleteTranslation).toLowerCase()}
                },
                subTitle: {
                    ...entityMessages.deleteEntitySubtitle,
                    values: {
                        entry: selectedEntity.name,
                    },
                },
                icon: ICON_MAP.DELETE as IconProp,
                confirmationMessage: {
                    ...entityMessages.confirmDeleteEntity,
                    values: {
                        entry: selectedEntity.name,
                    },
                },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: generalMessages.delete,
                callbackFn: () => {
                    if (selectedEntity) {
                        return deleteEntity({
                            parkingId,
                            id: selectedEntity.id,
                        });
                    }
                },
            }}
        >
            {children}
        </ConfirmationModal>
    );
}

export default DeleteAllowedTime;
